import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { UNIT_LABEL } from "./kitchenUtils";
import { ComponentsEditor } from "./ComponentsEditor";

const BASES = [["half_pan", "Half pan", "Full pan = 2× unless you set it"], ["person", "Per person", "Packages sold per guest"], ["each", "Per each", "Dozens, trays, pieces"]];

const LineRow = ({ ln, idx, ings, units, basis, onChange, onRemove }) => {
  const ing = ings.find((i) => i.id === ln.ingredient_id);
  const unitOpts = ing ? units[ing.measure] : [];
  const set = (k, v) => onChange(idx, { ...ln, [k]: v });
  const cls = "h-10 rounded-xl bg-white border-black/10";
  return (
    <div data-testid={`recipe-line-${idx}`} className="rounded-xl bg-brand-cream/40 border border-black/5 p-3 grid grid-cols-6 gap-2">
      <select value={ln.ingredient_id} onChange={(e) => { const i = ings.find((x) => x.id === e.target.value); onChange(idx, { ...ln, ingredient_id: e.target.value, unit: i ? i.unit : "" }); }}
        data-testid={`recipe-line-ingredient-${idx}`} className="col-span-6 sm:col-span-3 h-10 rounded-xl border border-black/10 px-2 text-[13px] bg-white">
        <option value="">Pick ingredient…</option>
        {ings.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
      </select>
      <Input type="number" step="any" min="0" placeholder="Qty" title="Amount" aria-label="Amount" value={ln.qty} onChange={(e) => set("qty", e.target.value)} data-testid={`recipe-line-qty-${idx}`} className={`col-span-3 sm:col-span-1 ${cls}`} />
      <select value={ln.unit} onChange={(e) => set("unit", e.target.value)} data-testid={`recipe-line-unit-${idx}`} className="col-span-3 sm:col-span-2 h-10 rounded-xl border border-black/10 px-2 text-[13px] bg-white">
        {unitOpts.map((u) => <option key={u} value={u}>{u === "each" && ing?.count_label ? ing.count_label : UNIT_LABEL[u]}</option>)}
      </select>
      {basis === "half_pan" && (
        <Input type="number" step="any" min="0" placeholder="Full pan qty (default 2×)" title="Full pan amount (blank = 2× half pan)" aria-label="Full pan amount" value={ln.full_pan_qty ?? ""} onChange={(e) => set("full_pan_qty", e.target.value)} data-testid={`recipe-line-full-${idx}`} className={`col-span-6 sm:col-span-2 ${cls}`} />
      )}
      {ing?.measure === "count" && (
        <>
          <Input type="number" min="2" max="24" placeholder="Cut each into… (e.g. 2)" title="Cut each piece into" aria-label="Cut each piece into" value={ln.cut_into ?? ""} onChange={(e) => set("cut_into", e.target.value)} data-testid={`recipe-line-cut-${idx}`} className={`col-span-3 sm:col-span-2 ${cls}`} />
          <Input placeholder="Cut pieces called (half-slices)" value={ln.cut_label || ""} onChange={(e) => set("cut_label", e.target.value)} data-testid={`recipe-line-cut-label-${idx}`} className={`col-span-3 sm:col-span-1 ${cls}`} />
        </>
      )}
      <button type="button" onClick={() => onRemove(idx)} data-testid={`recipe-line-remove-${idx}`} className="col-span-6 sm:col-span-1 justify-self-end p-2 rounded-full text-red-600 hover:bg-red-50" aria-label="Remove line"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
};

const num = (v) => (v === "" || v == null ? null : Number(v));
let keySeq = 0;
const newKey = () => `ln-${++keySeq}`;
const BASIS_HINT = { person: "per person", each: "per each" };

export const RecipeEditor = ({ item, ings, units, onSaved, onClose }) => {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/kitchen/recipes/${item.id}`).then((r) => setRec({
      ...r.data.recipe,
      lines: (r.data.recipe.lines || []).map((l) => ({ ...l, _key: newKey() })),
      basis: r.data.recipe.lines?.length ? r.data.recipe.basis : item.unit_label === "person" ? "person" : r.data.recipe.basis,
    }));
  }, [item]);
  if (!rec) return <Loader2 className="h-5 w-5 animate-spin text-brand-teal" />;
  const set = (k, v) => setRec((s) => ({ ...s, [k]: v }));
  const changeLine = (i, ln) => set("lines", rec.lines.map((x, j) => (j === i ? ln : x)));
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        basis: rec.basis, servings_per_unit: num(rec.servings_per_unit), portion_note: rec.portion_note || "", prep_notes: rec.prep_notes || "",
        source: "manual",
        components: (rec.components || []).filter((c) => c.name?.trim()).map((c) => ({ name: c.name.trim(), amount: num(c.amount), unit_label: c.unit_label || "" })),
        lines: rec.lines.filter((l) => l.ingredient_id).map((l) => ({
          ingredient_id: l.ingredient_id, qty: Number(l.qty), unit: l.unit,
          full_pan_qty: rec.basis === "half_pan" ? num(l.full_pan_qty) : null, cut_into: num(l.cut_into), cut_label: l.cut_label || "",
        })),
      };
      await api.put(`/admin/kitchen/recipes/${item.id}`, body);
      toast.success("Recipe saved");
      onSaved(item.id, body);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save recipe.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="recipe-editor" className="rounded-2xl bg-white border border-brand-teal/40 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-teal">Recipe</p>
          <p className="font-display font-black text-xl text-brand-charcoal">{item.name}</p>
        </div>
        <button type="button" onClick={onClose} data-testid="recipe-close" className="p-2 rounded-full bg-brand-cream" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid sm:grid-cols-3 gap-2" role="radiogroup">
        {BASES.map(([k, label, hint]) => (
          <button key={k} type="button" role="radio" aria-checked={rec.basis === k} onClick={() => set("basis", k)} data-testid={`recipe-basis-${k}`}
            className={`text-left rounded-xl border px-3 py-2 ${rec.basis === k ? "border-brand-teal bg-brand-teal/10" : "border-black/10"}`}>
            <span className="block text-[12px] font-bold text-brand-charcoal">Amounts {label.toLowerCase()}</span>
            <span className="block text-[11px] text-brand-charcoal/55">{hint}</span>
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input type="number" step="any" min="0" placeholder={`Servings ${rec.basis === "half_pan" ? "per half pan" : "per unit"} (optional)`} value={rec.servings_per_unit ?? ""} onChange={(e) => set("servings_per_unit", e.target.value)} data-testid="recipe-servings" className="h-10 rounded-xl border-black/10" />
        <Input placeholder="Portion size (e.g. 2 slices per guest)" value={rec.portion_note || ""} onChange={(e) => set("portion_note", e.target.value)} data-testid="recipe-portion" className="h-10 rounded-xl border-black/10" />
      </div>
      {ings.length === 0 && <p className="text-[12.5px] text-brand-gold-dark font-bold">Add raw ingredients first (Ingredients tab).</p>}
      {rec.lines.length > 0 && (
        <p className="text-[11px] text-brand-charcoal/55" data-testid="recipe-lines-legend">
          Each line: ingredient · amount {rec.basis === "half_pan" ? "per half pan · full pan amount (blank = 2×)" : BASIS_HINT[rec.basis]} · for sliced items, how many pieces each is cut into (e.g. 2 = half-slices).
        </p>
      )}
      <div className="space-y-2">
        {rec.lines.map((ln, i) => (
          <LineRow key={ln._key} ln={ln} idx={i} ings={ings} units={units} basis={rec.basis} onChange={changeLine} onRemove={(j) => set("lines", rec.lines.filter((_, x) => x !== j))} />
        ))}
      </div>
      <button type="button" onClick={() => set("lines", [...rec.lines, { ingredient_id: "", qty: "", unit: "", _key: newKey() }])} disabled={!ings.length} data-testid="recipe-add-line"
        className="w-full h-10 rounded-xl border border-dashed border-brand-charcoal/25 text-[11px] font-bold uppercase tracking-[0.1em] inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
        <Plus className="h-3.5 w-3.5" /> Add ingredient line
      </button>
      <ComponentsEditor comps={rec.components || []} basis={rec.basis} onChange={(c) => set("components", c)} />
      <Textarea rows={2} placeholder="Prep notes for the kitchen sheet (optional)" value={rec.prep_notes || ""} onChange={(e) => set("prep_notes", e.target.value)} data-testid="recipe-prep-notes" className="rounded-xl border-black/10" />
      <button type="button" onClick={save} disabled={busy} data-testid="recipe-save" className="w-full h-11 rounded-full bg-brand-charcoal text-white text-[12px] font-bold uppercase tracking-[0.1em] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Save recipe"}
      </button>
    </div>
  );
};

export default RecipeEditor;
